import { createContext, useContext, type ReactNode } from 'react';
import type { DayFirstFailure } from '../lib/dayKeyText.js';
import type { DateRangePresetId } from '../lib/dateRangePresets.js';

/* ===========================================================================
 * EVERY WORD THE DATE CONTROLS SAY, AS A PROP.
 *
 * `packages/ui` has no i18n and must not grow one — it is consumed by
 * apps/web, apps/installer and the kiosk, which do not share a bundle or even
 * a locale strategy. `Pagination` already answers this exact question with an
 * interface, an English default and a context provider, and this is the same
 * shape on purpose: an app wraps its tree once, and no screen passes twenty
 * strings down by hand.
 *
 * THE DEFAULTS ARE ENGLISH AND THEY ARE NOT A TRANSLATION LAYER. They exist so
 * a story, a test and the installer render readable words; a bilingual app is
 * expected to provide the whole object.
 * ======================================================================== */

export interface DateFieldLabels {
  /** Twelve, January-first, as the month header writes them. */
  monthNames: string[];
  /** Seven, MONDAY-FIRST — the column headings. Two letters: Mo Tu We … */
  weekdayShort: string[];
  /** Seven, Monday-first, spelled out. Never drawn; read aloud in the grid. */
  weekdayLong: string[];
  /** The empty trigger's text, and the format the reader is invited to type. */
  placeholder: string;
  /** The trigger's accessible name when the caller gives no label of its own. */
  triggerAria: string;
  previousMonth: string;
  nextMonth: string;
  /** The month picker's year header — `MonthField`. */
  previousYear: string;
  nextYear: string;
  /** The month picker's twelve-cell grid, as a group name. */
  monthGrid: string;
  /** `MonthField`'s accessible name when the caller gives none. */
  monthTriggerAria: string;
  today: string;
  clear: string;
  reset: string;
  apply: string;
  from: string;
  to: string;
  /** The preset rail's rows, keyed by id. */
  presets: Record<DateRangePresetId, string>;
  /** `(monthName, days) => 'September has 30 days.'` */
  monthLength: (monthName: string, days: number) => string;
  /** What to say for a string that is not a date at all. */
  unreadable: string;
  /** What to say for a month outside 1–12. */
  monthRange: string;
  /** `(earliest, latest) => 'Pick a day between 01-09-2026 and 30-09-2026.'` */
  outOfBounds: (min: string, max: string) => string;
  /** The time control's own words. */
  time: {
    placeholder: string;
    triggerAria: string;
    listAria: string;
    /** The clock glyph's name — the pointer's way into the list. */
    showList: string;
    unreadable: string;
  };
}

export const DEFAULT_DATE_LABELS: DateFieldLabels = {
  monthNames: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  weekdayShort: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
  weekdayLong: [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
  ],
  placeholder: 'DD-MM-YYYY',
  triggerAria: 'Date',
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  previousYear: 'Previous year',
  nextYear: 'Next year',
  monthGrid: 'Months',
  monthTriggerAria: 'Month',
  today: 'Today',
  clear: 'Clear',
  reset: 'Reset',
  apply: 'Apply',
  from: 'From',
  to: 'To',
  presets: {
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisQuarter: 'This quarter',
    custom: 'Custom…',
  },
  monthLength: (monthName, days) => `${monthName} has ${days} days.`,
  unreadable: 'Write the date as DD-MM-YYYY.',
  monthRange: 'A month is between 01 and 12.',
  outOfBounds: (min, max) => `Pick a day between ${min} and ${max}.`,
  time: {
    placeholder: 'HH:MM',
    triggerAria: 'Time',
    listAria: 'Times',
    showList: 'Show times',
    unreadable: 'Write the time as HH:MM, on a 24-hour clock.',
  },
};

const DateLabelsContext = createContext<DateFieldLabels>(DEFAULT_DATE_LABELS);

export function DateFieldLabelsProvider({
  labels,
  children,
}: {
  labels: DateFieldLabels;
  children: ReactNode;
}): JSX.Element {
  return <DateLabelsContext.Provider value={labels}>{children}</DateLabelsContext.Provider>;
}

/**
 * The labels in force, with a per-control `Partial` override on top. The
 * override is what lets one field say "Expires on" without the screen having to
 * restate the other nineteen strings.
 */
export function useDateFieldLabels(override?: Partial<DateFieldLabels>): DateFieldLabels {
  const base = useContext(DateLabelsContext);
  if (!override) return base;
  return { ...base, ...override, time: { ...base.time, ...override.time } };
}

/**
 * A parse failure turned into the sentence the reader sees.
 *
 * `empty` returns `null` deliberately: an empty field is a CLEARED field, not a
 * mistake, and putting an error under it would make deleting a value look like
 * a failure (design-rules §3.10 — say the consequence, and only when there is
 * one).
 */
export function failureMessage(
  failure: DayFirstFailure,
  labels: DateFieldLabels
): string | null {
  switch (failure.kind) {
    case 'empty':
      return null;
    case 'unreadable':
      return labels.unreadable;
    case 'month':
      return labels.monthRange;
    case 'monthLength':
      return labels.monthLength(labels.monthNames[failure.month - 1] ?? '', failure.days);
  }
}
